import { closePopover } from "./popovers";
import { pipelineRoot } from "./request";

const DAY = 86400000;
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const CHEVRON_LEFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 6l-6 6 6 6"/></svg>';
const CHEVRON_RIGHT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 6l6 6-6 6"/></svg>';

export const toIso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export function fromIso(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

const startOfToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const addDays = (date: Date, days: number): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

function nextMonday(from: Date): Date {
  const offset = ((8 - from.getDay()) % 7) || 7;
  return addDays(from, offset);
}

let current: HTMLElement | null = null;
let restoreFocus: HTMLElement | null = null;

export function closeDatePicker(): void {
  current?.remove();
  current = null;
  restoreFocus?.focus();
  restoreFocus = null;
}

function place(pop: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  const width = pop.offsetWidth;
  const height = pop.offsetHeight;
  const margin = 8;
  const clampX = (x: number) => Math.min(Math.max(margin, x), window.innerWidth - width - margin);
  const clampY = (y: number) => Math.min(Math.max(margin, y), window.innerHeight - height - margin);
  let left: number;
  let top: number;
  if (rect.bottom + 6 + height <= window.innerHeight - margin) {
    left = clampX(rect.right - width);
    top = rect.bottom + 6;
  } else if (rect.top - 6 - height >= margin) {
    left = clampX(rect.right - width);
    top = rect.top - 6 - height;
  } else {
    const roomLeft = rect.left - 8 - width >= margin;
    left = roomLeft ? rect.left - 8 - width : clampX(rect.right + 8);
    top = clampY(rect.top + rect.height / 2 - height / 2);
  }
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
}

export function openDatePicker(anchor: HTMLElement, value: string, onPick: (iso: string) => void): void {
  closePopover();
  closeDatePicker();
  const today = startOfToday();
  const selected = fromIso(value);
  let month = new Date((selected || today).getFullYear(), (selected || today).getMonth(), 1);
  let focusDate = selected || today;

  const pop = document.createElement("div");
  pop.className = "pl-cal";
  pop.setAttribute("role", "dialog");
  pop.setAttribute("aria-label", "Choose a due date");
  (pipelineRoot() || document.body).appendChild(pop);
  current = pop;
  restoreFocus = anchor;

  const pick = (iso: string) => {
    closeDatePicker();
    onPick(iso);
  };

  const render = () => {
    const label = month.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const first = (month.getDay() + 6) % 7;
    const start = addDays(month, -first);
    const cells: string[] = [];
    for (let i = 0; i < 42; i++) {
      const day = addDays(start, i);
      const iso = toIso(day);
      const classes = ["pl-cal-day"];
      if (day.getMonth() !== month.getMonth()) classes.push("is-outside");
      if (day.getTime() === today.getTime()) classes.push("is-today");
      if (selected && day.getTime() === selected.getTime()) classes.push("is-selected");
      if (day < today) classes.push("is-past");
      const focus = day.getTime() === focusDate.getTime();
      cells.push(
        `<button type="button" class="${classes.join(" ")}" data-pl-cal-day="${iso}" tabindex="${focus ? 0 : -1}" ` +
          `aria-label="${day.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}"` +
          `${selected && day.getTime() === selected.getTime() ? ' aria-pressed="true"' : ""}>${day.getDate()}</button>`,
      );
    }
    const quick = [
      ["Today", today],
      ["Tomorrow", addDays(today, 1)],
      ["Next Monday", nextMonday(today)],
      ["In 2 weeks", addDays(today, 14)],
    ] as const;
    pop.innerHTML =
      `<div class="pl-cal-quick">${quick
        .map(([text, date]) => `<button type="button" class="pl-cal-chip" data-pl-cal-day="${toIso(date)}">${text}</button>`)
        .join("")}</div>` +
      `<div class="pl-cal-head"><button type="button" class="pl-cal-nav" data-pl-cal-move="-1" aria-label="Previous month">${CHEVRON_LEFT}</button>` +
      `<span class="pl-cal-month">${label}</span>` +
      `<button type="button" class="pl-cal-nav" data-pl-cal-move="1" aria-label="Next month">${CHEVRON_RIGHT}</button></div>` +
      `<div class="pl-cal-grid" role="grid">${WEEKDAYS.map((d) => `<span class="pl-cal-wd">${d}</span>`).join("")}${cells.join("")}</div>` +
      `<div class="pl-cal-foot">` +
      (value ? `<button type="button" class="pl-cal-clear" data-pl-cal-day="">Remove due date</button>` : "<span></span>") +
      `<button type="button" class="pl-cal-close" data-pl-cal-close>Close</button></div>`;
    place(pop, anchor);
  };

  const focusDay = () => pop.querySelector<HTMLElement>(`.pl-cal-day[data-pl-cal-day="${toIso(focusDate)}"]`)?.focus();

  pop.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    event.stopPropagation();
    const day = target.closest<HTMLElement>("[data-pl-cal-day]");
    const move = target.closest<HTMLElement>("[data-pl-cal-move]");
    if (day) pick(day.dataset.plCalDay || "");
    else if (move) {
      month = new Date(month.getFullYear(), month.getMonth() + Number(move.dataset.plCalMove), 1);
      focusDate = new Date(month);
      render();
      pop.querySelector<HTMLElement>(`[data-pl-cal-move="${move.dataset.plCalMove}"]`)?.focus();
    } else if (target.closest("[data-pl-cal-close]")) closeDatePicker();
  });

  pop.addEventListener("keydown", (event) => {
    const steps: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeDatePicker();
    } else if (steps[event.key] && (event.target as HTMLElement).matches(".pl-cal-day")) {
      event.preventDefault();
      focusDate = addDays(focusDate, steps[event.key]);
      if (focusDate.getMonth() !== month.getMonth() || focusDate.getFullYear() !== month.getFullYear()) {
        month = new Date(focusDate.getFullYear(), focusDate.getMonth(), 1);
      }
      render();
      focusDay();
    }
  });

  render();
  focusDay();
}

let bound = false;

export function initDatePicker(): void {
  if (bound) return;
  bound = true;
  document.addEventListener("mousedown", (event) => {
    if (current && !current.contains(event.target as Node) && !(event.target as HTMLElement).closest("[data-pl-na-due]")) {
      closeDatePicker();
    }
  });
  window.addEventListener("resize", () => closeDatePicker());
  document.addEventListener("turbo:before-cache", () => closeDatePicker());
}

export const dayDiff = (iso: string): number | null => {
  const date = fromIso(iso);
  return date ? Math.round((date.getTime() - startOfToday().getTime()) / DAY) : null;
};
